import React, { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import TextareaAutosize from 'react-textarea-autosize';
import { Badge } from 'reactstrap';
import { InitOrganization, Me, Person } from '../../types';
import {
  calculateDescendantsStats,
  ObjectiveWithRef as Objective,
  Tag,
} from '../../utils/models/Objective';
import LinkedObjectives from '../Objectives/LinkedObjectives';
import ObjectiveCollaboratorsOrPlaceholder from '../Objectives/ObjectiveCollaboratorsOrPlaceholder';
import ObjectiveIndicator from '../Objectives/ObjectiveIndicator';
import {
  hasNoUsefulInformation,
  useObjectiveActions,
  useObjectiveAddTag,
} from '../Objectives/ObjectiveOperations';
import ObjectivesBreakdownBar from '../Objectives/ObjectivesBreakdownBar';
import ObjectiveTags from '../Objectives/ObjectiveTags';
import ObjectiveText from '../Objectives/ObjectiveText';
import ObjectiveTypeBadge from '../Objectives/ObjectiveTypeBadge';
import {
  getAllowedParentTypes,
  ObjectiveType,
  useCanShowParentLinking,
  useObjectiveItemTypeContext,
} from '../Objectives/ObjectiveTypeOperations';
import {
  ObjectiveItem,
  ObjectiveItemActions,
  ObjectiveItemBody,
  ObjectiveItemHead,
  ObjectiveItemHeaderRow,
  ObjectiveItemTagsRow,
  ObjectiveItemTitle,
} from './components';
import ObjectiveItemAddControls from './ObjectiveItemAddControls';
import ObjectiveItemMoreControls from './ObjectiveItemMoreControls';
import PersonalObjectiveAddItem from './PersonalObjectiveAddItem';
import PersonalObjectiveItem from './PersonalObjectiveItem';
import PersonalObjectiveItemCopyToNextPeriodModal from './PersonalObjectiveItemCopyToNextPeriodModal';
import PersonalObjectiveItemDeleteModal from './PersonalObjectiveItemDeleteModal';

const EMPTY_CHILDREN_TYPE_IDS: (number | null | undefined)[] = [];

interface Props {
  isRootElement?: boolean;
  objective: Objective;
  showLabelHeadings?: boolean;
  updateParentObjective?: (objective: Objective) => void;
  forceShowChildren?: boolean;
  currentOrganization: InitOrganization;
  me: Me;
  person: Person;
  isEditable?: boolean;
  isInPopover?: boolean;
  hideScoresAndWeight?: boolean;
  hideRelatedObjectives?: boolean;
  enableParentEdit: boolean;
  editableObjectiveIds?: ReadonlySet<number>;
  showDescendantsCount?: boolean;
  showDescendantsBreakdown?: boolean;
  applyFiltering?: boolean;
  textToHighlight?: string;
  autoFocus?: boolean;
  organizationObjectiveTypes: ObjectiveType[];
  userEditableObjectiveTypes: ObjectiveType[];
  // Show the type badge at the end of the title row instead of on the tags row.
  inlineTypeBadge?: boolean;
}

const PersonalObjectiveItemRoot: FC<Props> = ({
  objective,
  updateParentObjective = () => {
    //do nothing
  },
  forceShowChildren = false,
  currentOrganization,
  me,
  person,
  isEditable: isGlobalEditable = false,
  isInPopover = false,
  hideScoresAndWeight = false,
  hideRelatedObjectives = false,
  enableParentEdit,
  editableObjectiveIds,
  showDescendantsCount = false,
  showDescendantsBreakdown = false,
  applyFiltering = false,
  textToHighlight,
  autoFocus = false,
  organizationObjectiveTypes,
  userEditableObjectiveTypes,
  inlineTypeBadge = false,
}) => {
  const { formatMessage } = useIntl();
  const [value, setValue] = React.useState<Objective>(objective);

  const { totalDescendants, descedantsStatusBreakdown, matchDescendants } =
    calculateDescendantsStats(value);
  const [showChildren, setShowChildren] = React.useState<boolean>(
    () => forceShowChildren || matchDescendants
  );

  const { isIndividual, isEditable, canAddChild } = useObjectiveItemTypeContext(
    {
      objective: value,
      organizationObjectiveTypes,
      userEditableObjectiveTypes,
      isGlobalEditable,
      editableObjectiveIds,
    }
  );

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showCopyToNextPeriodModal, setShowCopyToNextPeriodModal] =
    useState<boolean>(false);
  const [indent] = useState<number>(0);

  const onChildChange = useCallback((objective) => {
    setValue(objective);
    if (objective.children.length == 0) {
      setShowChildren(false);
    }
  }, []);

  const {
    deleteObjective,
    addChild,
    updateRelatedObjectivesRoot,
    setRootParentObjective,
    updateIndicatorRoot,
    updateTextRoot,
    updateTagsRoot,
    updateObjectiveTypeRoot,
    updateTextKeyDownRoot,
  } = useObjectiveActions({
    me,
    person,
    firstDay: objective.coverage_start_date,
    lastDay: objective.coverage_end_date,
    currentOrganization,
    defaultObjectiveTypeId: undefined,
    userEditableObjectiveTypes,
  });

  const onChangeTagsRoot = useCallback(
    (tags: Tag[]) => updateTagsRoot({ tags, value, setValue }),
    [updateTagsRoot, value, setValue]
  );

  const onChangeTypeRoot = useCallback(
    (objectiveType: number | null) =>
      updateObjectiveTypeRoot({ objectiveType, value, setValue }),
    [updateObjectiveTypeRoot, value, setValue]
  );

  const childrenTypeIds = useMemo(
    () =>
      value.children?.map((c) => c.objective_type) ?? EMPTY_CHILDREN_TYPE_IDS,
    [value.children]
  );

  const allowedParentTypes = useMemo(
    () =>
      getAllowedParentTypes(organizationObjectiveTypes, value.objective_type),
    [organizationObjectiveTypes, value.objective_type]
  );

  const canShowParentLinking = useCanShowParentLinking(
    allowedParentTypes,
    !!value?.parent_objective
  );

  const onToggleItemCopyModal = useCallback(
    () => setShowCopyToNextPeriodModal(false),
    []
  );

  const { isAddingTag, setIsAddingTag, onClickAddTag } = useObjectiveAddTag();

  if (!isEditable && hasNoUsefulInformation(value)) {
    return null;
  }

  const typeBadge = (
    <ObjectiveTypeBadge
      objectiveTypeId={value.objective_type}
      organizationObjectiveTypes={organizationObjectiveTypes}
      collaborators={value.collaborators}
      isEditable={isEditable}
      onChangeType={onChangeTypeRoot}
      userEditableObjectiveTypes={userEditableObjectiveTypes}
      childrenTypeIds={childrenTypeIds}
      parentTypeId={value.parent_objective?.objective_type}
    />
  );

  return (
    <>
      <ObjectiveItem
        key={value.key}
        aria-label={'container-objective-' + value.key}
        indent={indent}
      >
        <ObjectiveItemHead>
          <PersonalObjectiveItemDeleteModal
            isOpen={showDeleteModal}
            onClosed={() => setShowDeleteModal(false)}
            toggle={() => setShowDeleteModal(!showDeleteModal)}
            objective={value}
            onDelete={({ deleteAllChildren, removeForAllCollaborators }) =>
              deleteObjective({
                value,
                updateParentObjective,
                deleteAllChildren,
                removeForAllCollaborators,
              })
            }
            me={me}
            person={person}
          />
          <PersonalObjectiveItemCopyToNextPeriodModal
            isOpen={showCopyToNextPeriodModal}
            onClosed={onToggleItemCopyModal}
            toggle={onToggleItemCopyModal}
            objective={value}
            organizationObjectiveTypes={organizationObjectiveTypes}
            person={person}
          />
          {!hideRelatedObjectives &&
            !!(value.name?.length > 0) &&
            canShowParentLinking && (
              <LinkedObjectives
                isParentObjective
                id={`parent-objective-${objective.key}`}
                textIfEmpty={formatMessage({
                  id: 'app.views.widget.inputs.objective_input.linked_objectives.parent.set_parent_objective',
                  defaultMessage: 'Set parent objective',
                })}
                prefixIfPresent={formatMessage({
                  id: 'app.views.widget.inputs.objective_input.linked_objectives.parent.child_of',
                  defaultMessage: 'Subobjective of',
                })}
                targetObjectiveKey={value.key}
                allowedParentTypes={allowedParentTypes}
                objectives={
                  value?.parent_objective ? [value.parent_objective] : []
                }
                owner_person={value.owner_person}
                coverageStartDate={value.coverage_start_date}
                coverageEndDate={value.coverage_end_date}
                isEditable={enableParentEdit && isEditable}
                callback={(rootParentObjectives) =>
                  setRootParentObjective({
                    rootParentObjectives,
                    value,
                    setValue,
                  })
                }
                maxElements={1}
              />
            )}
        </ObjectiveItemHead>
        <ObjectiveItemBody>
          <ObjectiveItemHeaderRow wrap={inlineTypeBadge}>
            <div
              className="flex-grow-1"
              style={{
                minWidth: 150,
                flexBasis: inlineTypeBadge ? 0 : undefined,
              }}
            >
              <ObjectiveItemTitle
                isInPopover={isInPopover}
                isEditable={isEditable}
                fullWidth={hideScoresAndWeight && !isInPopover && !isEditable}
                showChildren={showChildren}
                hasChildren={(value?.children?.length ?? 0) > 0}
                onClick={() =>
                  (value?.children?.length ?? 0) > 0 &&
                  setShowChildren(!showChildren)
                }
              >
                {isEditable ? (
                  <TextareaAutosize
                    disabled={!isEditable}
                    ref={value.ref}
                    autoFocus={autoFocus || value.isNew}
                    role="listitem"
                    aria-label={'objective-' + value.key}
                    name={'input-' + value.key}
                    // ref={(ref) => (value.ref = ref)} // TODO: check check!, never had the ref initialized
                    style={{ overflowY: 'hidden' }}
                    className={
                      'form-control form-control-flush fw-bold pb-0 mt-2 ' +
                      (value.name ? 'pt-0' : 'pt-2')
                    }
                    placeholder={formatMessage({
                      id: 'app.views.person.personal_objective_item_root.text_editor.placeholder',
                      defaultMessage: 'Write an objective name',
                    })}
                    value={value.name}
                    onChange={(e) => updateTextRoot({ e, value, setValue })}
                    onKeyDown={(e) =>
                      updateTextKeyDownRoot({
                        e,
                        value,
                        setShowDeleteModal,
                        updateParentObjective,
                        setValue,
                        setShowChildren,
                      })
                    }
                  />
                ) : (
                  <p
                    className={
                      'form-control form-control-flush mb-0 ' +
                      (!applyFiltering || value.matched ? 'fw-bold' : '')
                    }
                    role="listitem"
                    aria-label={'objective-' + value.key}
                    id={'input-' + value.key}
                  >
                    {showDescendantsCount && totalDescendants > 0 && (
                      <>
                        <Badge className="bg-light rounded-pill me-2">
                          {totalDescendants}
                        </Badge>
                      </>
                    )}
                    <ObjectiveText
                      value={value.name}
                      textToHighlight={textToHighlight}
                    />
                  </p>
                )}
              </ObjectiveItemTitle>
            </div>
            <ObjectiveItemActions>
              {isEditable && !hideScoresAndWeight && (
                <ObjectiveItemAddControls
                  isEditable={isEditable}
                  canAddChild={canAddChild}
                  setShowChildren={setShowChildren}
                  addChild={addChild}
                  value={value}
                  setValue={setValue}
                />
              )}
              {!hideScoresAndWeight && (
                <>
                  <ObjectiveCollaboratorsOrPlaceholder
                    objective={value}
                    disabled={!isEditable}
                    isIndividual={isIndividual}
                    onSave={(updatedChild) => {
                      setValue({
                        ...value,
                        collaborators: updatedChild.collaborators,
                      });
                    }}
                    isRootObjective={true}
                    profilePerson={person}
                    me={me}
                  />
                  <ObjectiveIndicator
                    disabled={!isEditable}
                    objective={value}
                    onChange={(updatedObj) =>
                      updateIndicatorRoot({ updatedObj, value, setValue })
                    }
                    me={me}
                  />
                </>
              )}
              {showDescendantsBreakdown && (
                <ObjectivesBreakdownBar breakdown={descedantsStatusBreakdown} />
              )}
              {isEditable && !hideScoresAndWeight && (
                <ObjectiveItemMoreControls
                  isEditable={isEditable}
                  canAddChild={canAddChild}
                  setShowChildren={setShowChildren}
                  setShowDeleteModal={setShowDeleteModal}
                  setShowCopyToNextPeriodModal={setShowCopyToNextPeriodModal}
                  addChild={addChild}
                  value={value}
                  setValue={setValue}
                  userEditableObjectiveTypes={userEditableObjectiveTypes}
                  organizationObjectiveTypes={organizationObjectiveTypes}
                  parentTypeId={value.parent_objective?.objective_type}
                  onChangeType={onChangeTypeRoot}
                  onClickAddTag={onClickAddTag}
                />
              )}
            </ObjectiveItemActions>
            {inlineTypeBadge && <div>{typeBadge}</div>}
          </ObjectiveItemHeaderRow>
          <ObjectiveItemTagsRow>
            {!inlineTypeBadge && typeBadge}
            <ObjectiveTags
              tags={value.tags || []}
              isEditable={isEditable}
              isAdding={isAddingTag}
              setIsAdding={setIsAddingTag}
              onChangeTags={onChangeTagsRoot}
            />
            {value.related_to &&
              value.related_to.length > 0 &&
              !hideRelatedObjectives &&
              value.name?.length > 0 && (
                <LinkedObjectives
                  id={`linked-objectives-${objective.key}`}
                  textIfEmpty={formatMessage({
                    id: 'app.views.widget.inputs.person_objectives.linked_objectives.related_to.editing',
                    defaultMessage: 'link related',
                  })}
                  objectives={value.related_to}
                  owner_person={value.owner_person}
                  coverageStartDate={value.coverage_start_date}
                  coverageEndDate={value.coverage_end_date}
                  isEditable={false}
                  callback={(relatedObjectives) => {
                    updateRelatedObjectivesRoot({
                      value,
                      setValue,
                      relatedObjectives,
                    });
                  }}
                />
              )}
          </ObjectiveItemTagsRow>
        </ObjectiveItemBody>
      </ObjectiveItem>
      {(isEditable || !!value?.children?.length) && (
        <>
          {!!value?.children?.length &&
            value.children.map((child) => (
              <PersonalObjectiveItem
                hidden={!showChildren}
                isEditable={isEditable}
                isInPopover={isInPopover}
                hideScoresAndWeight={hideScoresAndWeight}
                hideRelatedObjectives={hideRelatedObjectives}
                key={child.key}
                objective={child}
                parentObjective={value}
                updateParentObjective={onChildChange}
                person={person}
                indent={indent + 1}
                editableObjectiveIds={editableObjectiveIds}
                showDescendantsCount={showDescendantsCount}
                showDescendantsBreakdown={showDescendantsBreakdown}
                textToHighlight={textToHighlight}
                organizationObjectiveTypes={organizationObjectiveTypes}
                userEditableObjectiveTypes={userEditableObjectiveTypes}
                inlineTypeBadge={inlineTypeBadge}
              />
            ))}
          {isEditable && (
            <PersonalObjectiveAddItem
              hidden={!showChildren}
              indent={indent}
              onClick={() => addChild({ value, setValue, setShowChildren })}
              value={value}
            />
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  const { me, currentOrganization, features } = state;

  return {
    me,
    currentOrganization,
    features,
  };
};

export default connect(mapStateToProps)(React.memo(PersonalObjectiveItemRoot));
